import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import MathRenderer from '../components/math/MathRenderer';
import UserMenu from '../components/UserMenu';
import {
  AITutorGenerateResponse,
  AITutorGeneratedQuestion,
  aiTutorAPI,
} from '../services/api';
import { AITutorGenerationError, pollAITutorSet } from '../utils/aiTutorPolling';

const promptExamples = [
  'Hard Exam 2 optimisation',
  'Normal distribution, medium',
  'Chain rule with exponentials',
];

const errorMessage = (error: any): string => {
  if (error instanceof AITutorGenerationError) return error.safeDetail;
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail.message === 'string') return detail.message;
  return 'Mingu could not generate questions right now. Please try again.';
};

const curriculumCopy = (response: AITutorGenerateResponse | null) => {
  const curriculum = response?.curriculum_node;
  if (!curriculum) return 'Checked for VCE Methods alignment.';
  const parts = [curriculum.area_of_study, curriculum.topic, curriculum.subtopic].filter(Boolean);
  return parts.length ? `Focus: ${parts.join(' - ')}` : 'Checked for VCE Methods alignment.';
};

const GeneratedQuestion = ({ question }: { question: AITutorGeneratedQuestion }) => {
  const [showSolution, setShowSolution] = useState(false);

  return (
    <article className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="mb-4 flex items-center justify-between gap-4">
        <span className="text-sm font-semibold text-subject-600 dark:text-subject-400">
          Question {question.position}
        </span>
        <span className="text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
          {question.question_type}
        </span>
      </div>
      <div className="text-left text-gray-900 dark:text-white">
        <MathRenderer latex={question.question_latex} />
      </div>
      {question.question_type === 'MC' && (
        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          {(['A', 'B', 'C', 'D'] as const).map((key) => (
            <div key={key} className="flex gap-2 rounded-md bg-gray-50 p-3 text-left dark:bg-gray-700/60">
              <strong className="text-subject-700 dark:text-subject-300">{key}.</strong>
              <MathRenderer latex={question.options[key] || ''} />
            </div>
          ))}
        </div>
      )}
      <button
        type="button"
        onClick={() => setShowSolution((value) => !value)}
        className="mt-4 rounded-full border border-subject-300 px-4 py-2 text-sm font-semibold text-subject-700 transition-colors hover:bg-subject-50 dark:border-subject-700 dark:text-subject-300 dark:hover:bg-subject-900/20"
      >
        {showSolution ? 'Hide solution' : 'Show solution'}
      </button>
      {showSolution && (
        <div className="mt-4 border-t border-gray-200 pt-4 text-left text-gray-900 dark:border-gray-700 dark:text-white">
          <p className="mb-2 text-sm font-semibold">Answer: {question.correct_answer}</p>
          <MathRenderer latex={question.solution_latex} />
          {question.marking_guide_latex && (
            <div className="mt-3">
              <p className="mb-1 text-sm font-semibold">Marking guide</p>
              <MathRenderer latex={question.marking_guide_latex} />
            </div>
          )}
        </div>
      )}
    </article>
  );
};

const Mingu: React.FC = () => {
  const [prompt, setPrompt] = useState('');
  const [lastPrompt, setLastPrompt] = useState('');
  const [result, setResult] = useState<AITutorGenerateResponse | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationDetail, setGenerationDetail] = useState('Mingu is thinking...');
  const [error, setError] = useState<string | null>(null);
  const requestId = useRef(0);

  useEffect(
    () => () => {
      requestId.current += 1;
    },
    [],
  );

  const generate = async () => {
    const trimmedPrompt = prompt.trim();
    if (trimmedPrompt.length < 3 || isGenerating) return;
    const activeRequest = requestId.current + 1;
    requestId.current = activeRequest;
    setIsGenerating(true);
    setGenerationDetail('Mingu is thinking...');
    setError(null);
    setResult(null);
    setLastPrompt(trimmedPrompt);

    try {
      const accepted = await aiTutorAPI.generateQuestions(trimmedPrompt);
      const completed = accepted.status === 'pending'
        ? await pollAITutorSet(accepted.set_id, aiTutorAPI.getGeneratedSet, {
            isCancelled: () => requestId.current !== activeRequest,
            onUpdate: (update) => setGenerationDetail(update.status_detail),
          })
        : accepted;
      if (completed && requestId.current === activeRequest) {
        setResult(completed);
      }
    } catch (caught) {
      if (requestId.current === activeRequest) {
        setError(errorMessage(caught));
      }
    } finally {
      if (requestId.current === activeRequest) {
        setIsGenerating(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <header className="bg-white shadow dark:bg-gray-800">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-6 sm:px-6 lg:px-8">
          <div className="text-left">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Mingu AI Tutor</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">Generate checked VCE Methods practice.</p>
          </div>
          <UserMenu />
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
        <Link to="/dashboard" className="mb-6 inline-block text-sm font-medium text-subject-700 hover:underline dark:text-subject-300">
          Back to dashboard
        </Link>
        <section className="rounded-xl bg-white p-6 text-left shadow dark:bg-gray-800">
          <label htmlFor="mingu-prompt" className="text-lg font-semibold text-gray-900 dark:text-white">
            What would you like to practise?
          </label>
          <textarea
            id="mingu-prompt"
            value={prompt}
            disabled={isGenerating}
            onChange={(event) => setPrompt(event.target.value)}
            placeholder="e.g. Give me hard Exam 2 optimisation questions"
            className="mt-3 min-h-32 w-full rounded-lg border border-gray-300 bg-white p-4 text-gray-900 focus:border-subject-500 focus:outline-none focus:ring-2 focus:ring-subject-200 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
          />
          <div className="mt-3 flex flex-wrap gap-2">
            {promptExamples.map((example) => (
              <button
                key={example}
                type="button"
                disabled={isGenerating}
                onClick={() => setPrompt(example)}
                className="rounded-full bg-subject-50 px-3 py-2 text-sm font-medium text-subject-700 hover:bg-subject-100 disabled:opacity-60 dark:bg-subject-900/20 dark:text-subject-300"
              >
                {example}
              </button>
            ))}
          </div>
          <button
            type="button"
            disabled={prompt.trim().length < 3 || isGenerating}
            onClick={() => void generate()}
            className="mt-5 rounded-lg bg-subject-600 px-5 py-3 font-semibold text-white hover:bg-subject-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isGenerating ? 'Mingu is working...' : 'Generate 1 question'}
          </button>
        </section>

        {isGenerating && (
          <section className="mt-6 rounded-xl border border-subject-200 bg-subject-50 p-5 text-left dark:border-subject-800 dark:bg-subject-900/20">
            <h2 className="font-semibold text-subject-800 dark:text-subject-200">{generationDetail}</h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              The page will update automatically when the question has been validated.
            </p>
          </section>
        )}

        {error && (
          <section className="mt-6 rounded-xl border border-red-200 bg-red-50 p-5 text-left text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
            {error}
          </section>
        )}

        {result && (
          <section className="mt-8">
            <div className="mb-5 text-left">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Generated by Mingu</h2>
              <p className="mt-1 text-sm font-medium text-subject-700 dark:text-subject-300">{curriculumCopy(result)}</p>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Prompt: {lastPrompt}</p>
            </div>
            <div className="space-y-4">
              {result.generated_questions.map((question) => (
                <GeneratedQuestion key={question.id} question={question} />
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default Mingu;
